// import './i18n.js'; // Admin은 다국어 지원 불필요
import React, { useCallback, useState, useEffect, useRef } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useUser } from "./contexts/UserContext.jsx";
import { signOut } from "firebase/auth";
import { auth, db } from "./firebase/firebaseConfig.jsx";
import { doc, getDoc } from "firebase/firestore";
import { appId } from "./firebase/firebaseConfig.jsx";

import Sidebar from "./components/Sidebar.jsx";
import AdminDashboard from "./pages/AdminDashboard.jsx";
import UserManagementPage from "./pages/UserManagementPage.jsx";
import RemittanceAccountManager from "./pages/RemittanceAccountManager.jsx";
import AdminKycPage from "./pages/AdminKycPage.jsx";
import AdminConsultationDashboard from "./pages/AdminConsultationDashboard.jsx";
import SettlementManagementPage from "./pages/SettlementManagementPage.jsx";
import AdminNotificationPage from "./pages/AdminNotificationPage.jsx";
import LoanAdminPage from "./pages/LoanAdminPage.jsx";
import FinanceSettingsPage from "./pages/FinanceSettingsPage.jsx";
import AdminNoticePage from "./pages/AdminNoticePage.jsx";
import CommunityManagementPage from "./pages/CommunityManagementPage.jsx";
import AdminUserManagementPage from "./pages/AdminUserManagementPage.jsx";
import ApiIntegrationMonitoringPage from "./pages/ApiIntegrationMonitoringPage.jsx";
import ChatCleanupPage from "./pages/ChatCleanupPage.jsx";
import MessageManagementPage from "./pages/MessageManagementPage.jsx";
import HomePageManagementPage from "./pages/HomePageManagementPage.jsx";
import DocumentExpiryManagementPage from "./pages/DocumentExpiryManagementPage.jsx";
import InAppMessagingManager from "./components/InAppMessagingManager.jsx";
import PolicyCompliancePage from "./pages/PolicyCompliancePage.jsx";
import RegionalPOIMonitorPage from "./pages/RegionalPOIMonitorPage.jsx";
import ConsultationFAQManagementPage from "./pages/ConsultationFAQManagementPage.jsx";
import ConsultationQuotaMonitorPage from "./pages/ConsultationQuotaMonitorPage.jsx";
import GovAccessManagementPage from "./pages/GovAccessManagementPage.jsx";
import MarketplaceManagementPage from "./pages/MarketplaceManagementPage.jsx";
import AdminRccApplicationsPage from "./pages/AdminRccApplicationsPage.jsx";
import AdminLoginPage from "./pages/AdminLoginPage.jsx";
import ProtectedRoute from "./components/ProtectedRoute.jsx";
import KioskDisplay from "./components/KioskDisplay.jsx";
import { ADMIN_EMAIL_ALLOWLIST, canAccessAdminPortal } from "./utils/adminPermissions";


const LoadingScreen = () => (
  <div className="flex justify-center items-center h-screen text-lg font-medium">
    Loading...
  </div>
);

const OfflineErrorScreen = ({ onRetry, isRetrying }) => {
  return (
    <div className="flex flex-col justify-center items-center h-screen bg-gray-100">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
        <div className="text-center mb-6">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">연결 오류</h1>
          <p className="text-gray-600">
            Firestore 서버에 연결할 수 없습니다.
          </p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800">
            네트워크 연결을 확인하고 다시 시도해주세요. 
            인터넷 연결이 정상이라면 잠시 후 자동으로 재시도됩니다.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={onRetry}
            disabled={isRetrying}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            {isRetrying ? "재시도 중..." : "다시 시도"}
          </button>
          <button
            onClick={() => window.location.reload()}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors"
          >
            페이지 새로고침
          </button>
        </div>
      </div>
    </div>
  );
};


const AdminLayout = ({ adminUser, children, onLogout, isCollapsed, onToggle }) => {
  const location = useLocation();
  const isGovSurface = location.pathname === '/' || location.pathname === '/kyc';
  
  return (
    <div className={`flex h-screen font-sans overflow-hidden ${isGovSurface ? 'bg-[#050d1a]' : 'bg-gray-100'}`}>
      <Sidebar adminUser={adminUser} onLogout={onLogout} isCollapsed={isCollapsed} onToggle={onToggle} />
      <div className="flex flex-col flex-1 overflow-hidden relative">
        <main className={`flex-1 overflow-auto ${isGovSurface ? 'bg-[#050d1a]' : ''}`}>{children}</main>
      </div>
    </div>
  );
};

const AdminApp = () => {
  const { user, profile, loading } = useUser();
  const location = useLocation();
  const isDisplay = location.pathname === '/display';
  const loginPath = isDisplay ? '/login?next=%2Fdisplay' : '/login';
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [hasCheckedOffline, setHasCheckedOffline] = useState(false);
  const lastConnectionCheckRef = useRef(0);

  // 오프라인 상태 확인 및 재시도 (최적화: 불필요한 호출 방지)
  const checkConnection = useCallback(async () => {
    if (!user?.uid) return false;
    
    // 마지막 확인 후 5초 이내면 스킵 (과도한 호출 방지)
    const now = Date.now();
    if (now - lastConnectionCheckRef.current < 5000) {
      return !isOffline;
    }
    lastConnectionCheckRef.current = now;
    
    try {
      setIsRetrying(true);
      // 간단한 Firestore 읽기로 연결 확인
      const testDoc = await getDoc(doc(db, `artifacts/${appId}/users`, user.uid));
      setIsOffline(false);
      setRetryCount(0);
      return true;
    } catch (error) {
      const isOfflineError = error.code === 'unavailable' || 
                            error.code === 'failed-precondition' ||
                            error.message?.includes('offline') ||
                            error.message?.includes('network') ||
                            error.message?.includes('connection');
      
      if (isOfflineError) {
        setIsOffline(true);
        return false;
      }
      // 다른 오류는 무시 (인증 오류 등)
      return false;
    } finally {
      setIsRetrying(false);
    }
  }, [user?.uid, isOffline]);

  // 사용자는 인증되었지만 프로필이 없는 경우 연결 확인 (한 번만)
  useEffect(() => {
    if (user && !profile && !hasCheckedOffline) {
      setHasCheckedOffline(true);
      checkConnection();
    }
  }, [user, profile, hasCheckedOffline, checkConnection]);

  // 주기적으로 연결 확인 (60초마다, 오프라인 상태일 때만)
  useEffect(() => {
    if (!user || !profile) return;
    
    // 오프라인 상태일 때만 주기적 재시도 (60초마다)
    const interval = setInterval(() => {
      if (isOffline) {
        checkConnection();
      }
    }, 60000);
    
    return () => clearInterval(interval);
  }, [user?.uid, profile?.uid, isOffline, checkConnection]);

  // 수동 재시도
  const handleRetry = useCallback(async () => {
    setRetryCount(prev => prev + 1);
    await checkConnection();
  }, [checkConnection]);

  // 로그아웃 함수
  const handleLogout = useCallback(async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🚪 Admin 로그아웃 시작...');
      }
      await signOut(auth);
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Admin 로그아웃 완료');
      }
      
      // 로그인 페이지로 리다이렉트
      window.location.href = '/login';
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ 로그아웃 실패:', error);
      }
    }
  }, []);

  // 사이드바 토글 함수
  const handleToggle = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  // 사용자는 인증되었지만 프로필이 없는 경우
  // 오프라인 상태일 수 있으므로 확인
  if (user && !profile) {
    // 오프라인 상태면 오프라인 UI 표시, 아니면 로그인으로 리다이렉트
    if (isOffline) {
      return <OfflineErrorScreen onRetry={handleRetry} isRetrying={isRetrying} />;
    }
    // 오프라인이 아닌데 프로필이 없으면 인증 오류로 간주
    return <Navigate to={loginPath} replace />;
  }

  const isAllowlistedEmail = user?.email && ADMIN_EMAIL_ALLOWLIST.has(user.email.toLowerCase());

  // 인증되지 않은 경우 로그인 페이지로 리다이렉트
  if (!user || !profile || (!isAllowlistedEmail && !canAccessAdminPortal(profile))) {
    return <Navigate to={loginPath} replace />;
  }

  if (isDisplay) {
    return (
      <KioskDisplay>
        <ProtectedRoute requiredPath="/"><AdminDashboard /></ProtectedRoute>
      </KioskDisplay>
    );
  }

  return (
    <AdminLayout adminUser={profile} onLogout={handleLogout} isCollapsed={isCollapsed} onToggle={handleToggle}>
      <Routes>
        <Route path="/" element={<ProtectedRoute requiredPath="/"><AdminDashboard /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute requiredPath="/users"><UserManagementPage /></ProtectedRoute>} />
        <Route path="/permissions" element={<ProtectedRoute requiredPath="/permissions"><AdminUserManagementPage /></ProtectedRoute>} />
        <Route path="/remittance-accounts" element={<ProtectedRoute requiredPath="/remittance-accounts"><RemittanceAccountManager /></ProtectedRoute>} />
        <Route path="/kyc" element={<ProtectedRoute requiredPath="/kyc"><AdminKycPage /></ProtectedRoute>} />
        <Route path="/rcc-issuance" element={<ProtectedRoute requiredPath="/rcc-issuance"><AdminRccApplicationsPage /></ProtectedRoute>} />
        <Route path="/finance-settings" element={<ProtectedRoute requiredPath="/finance-settings"><FinanceSettingsPage /></ProtectedRoute>} />
        <Route path="/consultations" element={<ProtectedRoute requiredPath="/consultations"><AdminConsultationDashboard /></ProtectedRoute>} />
        <Route path="/settlement-management" element={<ProtectedRoute requiredPath="/settlement-management"><SettlementManagementPage /></ProtectedRoute>} />
        <Route path="/api-integration-monitoring" element={<ProtectedRoute requiredPath="/api-integration-monitoring"><ApiIntegrationMonitoringPage /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute requiredPath="/notifications"><AdminNotificationPage /></ProtectedRoute>} />
        <Route path="/document-expiry-management" element={<ProtectedRoute requiredPath="/document-expiry-management"><DocumentExpiryManagementPage /></ProtectedRoute>} />
        <Route path="/loan-admin" element={<ProtectedRoute requiredPath="/loan-admin"><LoanAdminPage /></ProtectedRoute>} />
        <Route path="/notices" element={<ProtectedRoute requiredPath="/notices"><AdminNoticePage /></ProtectedRoute>} />
        <Route path="/message-management" element={<ProtectedRoute requiredPath="/message-management"><MessageManagementPage /></ProtectedRoute>} />
        <Route path="/homepage-management" element={<ProtectedRoute requiredPath="/homepage-management"><HomePageManagementPage /></ProtectedRoute>} />
        <Route path="/community-management" element={<ProtectedRoute requiredPath="/community-management"><CommunityManagementPage /></ProtectedRoute>} />
        <Route path="/marketplace-management" element={<ProtectedRoute requiredPath="/marketplace-management"><MarketplaceManagementPage /></ProtectedRoute>} />
        <Route path="/regional-poi-monitor" element={<ProtectedRoute requiredPath="/regional-poi-monitor"><RegionalPOIMonitorPage /></ProtectedRoute>} />
        <Route path="/chat-cleanup" element={<ProtectedRoute requiredPath="/chat-cleanup"><ChatCleanupPage /></ProtectedRoute>} />
        <Route path="/in-app-messaging" element={<ProtectedRoute requiredPath="/in-app-messaging"><InAppMessagingManager /></ProtectedRoute>} />
        <Route path="/policy-compliance" element={<ProtectedRoute requiredPath="/policy-compliance"><PolicyCompliancePage /></ProtectedRoute>} />
        <Route path="/consultation-faq-management" element={<ProtectedRoute requiredPath="/consultation-faq-management"><ConsultationFAQManagementPage /></ProtectedRoute>} />
        <Route path="/gov-access" element={<ProtectedRoute requiredPath="/gov-access"><GovAccessManagementPage /></ProtectedRoute>} />
        <Route path="/consultation-quota-monitor" element={<ProtectedRoute requiredPath="/consultation-quota-monitor"><ConsultationQuotaMonitorPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AdminLayout>
  );
};

export default AdminApp;
